/**
 * Vínculo trabalhista do colaborador — espelho de
 * `packages/domain/src/pessoas/vinculo-trabalhista.ts`.
 *
 * O web não importa `@deca/domain` (`docs/arquitetura.md`), então a lista vive
 * aqui também, como `PapelUsuario` em `servicos/jwt.ts`. Se ela mudar no
 * domínio, muda aqui — e a API recusa o que estiver fora da lista dela, então
 * uma divergência aparece como erro de validação, não como dado torto.
 *
 * É a lista do Deca 1 (`EmpresaEmployers.js`), com múltipla escolha: "Sócio" e
 * "Decisor" ao mesmo tempo é o caso comum. O cronograma escolhe destinatários
 * por estes rótulos — "Decisor" recebe aprovação, "Contábil" e "Financeiro"
 * recebem cobrança —, por isso o campo deixou de ser texto livre.
 */
export const VINCULOS_TRABALHISTAS = [
  'Sócio',
  'Decisor',
  'Financeiro',
  'Contábil',
  'Outros',
] as const;

export type VinculoTrabalhista = (typeof VINCULOS_TRABALHISTAS)[number];

/** A coluna guarda os rótulos unidos por vírgula, como o Deca 1 gravava. */
export function separarVinculos(texto: string | null | undefined): string[] {
  if (texto === null || texto === undefined) return [];
  return texto
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');
}

/** Ordem da lista, sem repetição — o mesmo que a API grava. */
export function juntarVinculos(vinculos: readonly string[]): string {
  const escolhidos = new Set(vinculos);
  return VINCULOS_TRABALHISTAS.filter((v) => escolhidos.has(v)).join(', ');
}

export function vinculosForaDaLista(texto: string | null | undefined): string[] {
  return separarVinculos(texto).filter(
    (v) => !(VINCULOS_TRABALHISTAS as readonly string[]).includes(v),
  );
}

/** `(DD) 9XXXX-XXXX` / `(DD) XXXX-XXXX`; o que não couber sai como veio. */
export function formatarTelefone(valor: string | null | undefined): string {
  if (typeof valor !== 'string') return '';
  const digitos = valor.replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');

  if (digitos.length === 11) return digitos.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  if (digitos.length === 10) return digitos.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  return valor;
}
